import { FormDrawer, FormDrawerContent, IconButton, isSuccess, useOpen, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ApplicationUserGroupMultiSelect } from "../applications/ApplicationUserGroupMultiSelect";
import { createApplicationUserService } from "../shared/api/userService";
import { IApplicationUserFormValues } from "../shared/types/user";
import { ApplicationUserMultiSelect } from "./ApplicationUserMultiSelect";

const initialValues: IApplicationUserFormValues = { users: [], userGroups: [] };

export const ApplicationUserAttachAction: FC = () => {
	const { open, handleOpen, handleClose } = useOpen();
	const { applicationId = "" } = useParams<"applicationId">();
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const applicationUserService = useMemo(() => createApplicationUserService(applicationId), [applicationId]);
	const queryClient = useQueryClient();

	const attachApplicationUsers = useCallback(
		async (values: IApplicationUserFormValues) => {
			const response = await applicationUserService.attachUsers({
				userIds: values.users.map((user) => user.id),
				userGroupIds: values.userGroups.map((userGroup) => userGroup.id),
			});
			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("applications") });
				showToast({ type: "success", body: t("Users were successfully attached."), title: t("Attached users") });
				return true;
			}

			return false;
		},
		[applicationUserService, queryClient, showToast, t],
	);

	const validate = useCallback(
		(values: IApplicationUserFormValues) => {
			const errors: { [K in keyof IApplicationUserFormValues]?: any } = {};

			if (values.users.length === 0 && values.userGroups.length === 0) {
				errors.users = t("Required field");
			}
			return errors;
		},
		[t],
	);

	return (
		<>
			<IconButton title={t("Attach users")} onClick={handleOpen} color="primary" size="small" icon="plus" />
			<FormDrawer
				open={open}
				validate={validate}
				onSubmit={attachApplicationUsers}
				initialValues={initialValues}
				onClose={handleClose}
				title={t("Attach users")}
				renderForm={(props) => (
					<FormDrawerContent {...props}>
						<ApplicationUserMultiSelect applicationId={applicationId} name="users" label={t("New users")} />
						<ApplicationUserGroupMultiSelect
							applicationId={applicationId}
							name="userGroups"
							label={t("New user groups")}
						/>
					</FormDrawerContent>
				)}
			/>
		</>
	);
};
